import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import SidebarPanel from '../admin/SidebarPanel';
import { adminApi } from '../../services/api';
import { useIsAnkrAdmin } from '../../context/AuthContext';
import VoiceAITranscriptViewer from '../shared/VoiceAITranscriptViewer';

const VALID_TABS = ['summary', 'transcript', 'metrics', 'media', 'raw'];

const RetellCallViewer = ({ isOpen, onClose, callId, callData: initialCallData }) => {
  const isAnkrAdmin = useIsAnkrAdmin();
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract tab from URL search params
  const urlTab = searchParams.get('tab');
  const initialTab = VALID_TABS.includes(urlTab) ? urlTab : 'summary';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [callData, setCallData] = useState(initialCallData || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [retryMessage, setRetryMessage] = useState('');

  // Cache for call data to avoid redundant API calls
  const [callDataCache, setCallDataCache] = useState({});

  // Sync activeTab with URL changes
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (VALID_TABS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    } else if (!urlTab && activeTab !== 'summary') {
      setActiveTab('summary');
    }
  }, [searchParams, activeTab]);

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const loadCallData = useCallback(async (forceRefresh = false) => {
    // Check cache first unless force refresh
    if (!forceRefresh && callDataCache[callId]) {
      setCallData(callDataCache[callId]);
      setError(null);
      setRetryMessage('');
      return;
    }

    const maxRetries = 3;
    let attempt = 0;

    while (attempt <= maxRetries) {
      try {
        setLoading(true);
        setError(null);

        if (attempt > 0) {
          setRetryMessage(`Sorry Captain. Seems like one of our servers is busy, retrying... (Attempt ${attempt + 1}/${maxRetries + 1})`);
        }

        const response = await adminApi.getRetellCallDetails(callId);

        if (response.data.success) {
          const data = response.data.data;
          setCallData(data);
          // Cache the successful response
          setCallDataCache(prev => ({ ...prev, [callId]: data }));
          setRetryMessage('');
          setRetryCount(0);
          return; // Success - exit retry loop
        } else {
          setError(response.data.error || 'Failed to load call data');
          setRetryMessage('');
          return;
        }
      } catch (err) {
        // Don't retry if request was aborted
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          setRetryMessage('');
          return;
        }

        const status = err.response?.status;

        // Retry on 429 (rate limit) or 5xx errors
        if ((status === 429 || status >= 500) && attempt < maxRetries) {
          attempt++;
          setRetryCount(attempt);

          // Exponential backoff with random jitter: (2^attempt * 1000) + random(0-1000)ms
          const baseDelay = Math.pow(2, attempt) * 1000;
          const jitter = Math.random() * 1000;
          const delay = baseDelay + jitter;

          setRetryMessage(`Sorry Captain. Seems like one of our servers is busy, retrying in ${(delay / 1000).toFixed(1)}s... (Attempt ${attempt + 1}/${maxRetries + 1})`);
          await sleep(delay);
        } else {
          // Final error - no more retries
          setRetryMessage('');

          if (status === 429) {
            setError('Sorry Captain. Seems like our servers are busy. Please try again in a few moments.');
          } else if (status === 404) {
            setError('Call details not found. The call may still be processing.');
          } else if (status >= 500) {
            setError('Server error. Please try again later.');
          } else {
            setError(err.response?.data?.error || err.message || 'Error loading call data');
          }
          return;
        }
      } finally {
        setLoading(false);
      }
    }
  }, [callId, callDataCache]);

  // Clear stale data immediately when callId changes
  useEffect(() => {
    if (callId) {
      // Clear old data to prevent showing stale content
      setCallData(null);
      setError(null);
    }
  }, [callId]);

  // Load call data if only callId is provided
  useEffect(() => {
    if (isOpen && callId && !initialCallData) {
      loadCallData(false);
    } else if (initialCallData) {
      setCallData(initialCallData);
    }
  }, [isOpen, callId, initialCallData, loadCallData]);

  const copyToClipboard = (text, label = 'Text') => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const badges = {
      'registered': 'bg-secondary',
      'ongoing': 'bg-primary',
      'ended': 'bg-success',
      'error': 'bg-danger',
      'canceled': 'bg-warning'
    };
    return badges[status] || 'bg-secondary';
  };

  const getSentimentBadge = (sentiment) => {
    const badges = {
      'Positive': 'bg-success',
      'Neutral': 'bg-secondary',
      'Negative': 'bg-danger'
    };
    return badges[sentiment] || 'bg-secondary';
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    // Update URL query params to reflect current tab
    const newParams = new URLSearchParams(searchParams);

    if (tabId === 'summary') {
      // Remove tab param for default tab
      newParams.delete('tab');
    } else {
      newParams.set('tab', tabId);
    }

    setSearchParams(newParams, { replace: true });
  };

  const handleClose = () => {
    // Clear tab query param when closing
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('tab');
    setSearchParams(newParams, { replace: true });
    // Reset to summary tab
    setActiveTab('summary');
    // Call original onClose
    onClose();
  };

  const renderSummaryTab = () => {
    if (!callData) return null;

    return (
      <div className="p-3">
        {/* Call Status */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center card-title text-primary mb-3">
                Call Status
              <span className={`badge ${getStatusBadge(callData.call_status)}`}>
                {callData.call_status}
              </span>
            </div>

            <div className="row">
              <div className="col-md-6">
                <p className="mb-2">
                  <strong>Call ID:</strong>
                  <br />
                  <small className="text-muted font-monospace">{callData.call_id}</small>
                  <button
                    className="btn btn-sm btn-link p-0 ms-2"
                    onClick={() => copyToClipboard(callData.call_id, 'Call ID')}
                    title="Copy Call ID"
                  >
                    <i className={`fas ${copied === 'Call ID' ? 'fa-check text-success' : 'fa-copy'}`}></i>
                  </button>
                </p>
              </div>
              <div className="col-md-6">
                <p className="mb-2">
                  <strong>Duration:</strong> {formatDuration(callData.duration_ms)}
                </p>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <p className="mb-2">
                  <strong>Started:</strong> {formatTimestamp(callData.start_timestamp)}
                </p>
              </div>
              <div className="col-md-6">
                <p className="mb-2">
                  <strong>Ended:</strong> {formatTimestamp(callData.end_timestamp)}
                </p>
              </div>
            </div>

            {callData.direction && (
              <p className="mb-2">
                <strong>Direction:</strong>{' '}
                <span className="badge bg-info">{callData.direction}</span>
              </p>
            )}

            {callData.from_number && callData.to_number && (
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-2">
                    <strong>From:</strong> {callData.from_number}
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="mb-2">
                    <strong>To:</strong> {callData.to_number}
                  </p>
                </div>
              </div>
            )}

            {callData.disconnection_reason && (
              <p className="mb-0">
                <strong>Disconnection Reason:</strong>{' '}
                <span className="text-muted">{callData.disconnection_reason}</span>
              </p>
            )}
          </div>
        </div>

        {/* Agent Info */}
        {callData.agent_name && (
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title text-primary mb-3">
                Agent Information
              </h6>
              <p className="mb-2">
                <strong>Name:</strong> {callData.agent_name}
              </p>
              {callData.agent_id && (
                <p className="mb-2">
                  <strong>ID:</strong>{' '}
                  <small className="text-muted font-monospace">{callData.agent_id}</small>
                </p>
              )}
              {callData.agent_version && (
                <p className="mb-0">
                  <strong>Version:</strong> {callData.agent_version}
                </p>
              )}

              {callData.retell_llm_dynamic_variables && Object.keys(callData.retell_llm_dynamic_variables).length > 0 && (
                <>
                  <hr />
                  <h6 className="text-muted mt-2 mb-2">Dynamic Variables</h6>
                  {Object.entries(callData.retell_llm_dynamic_variables).map(([key, value]) => (
                    <p key={key} className="mb-1">
                      <strong>{key}:</strong> {value}
                    </p>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* Call Analysis */}
        {callData.call_analysis && (
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title text-primary mb-3">
                Call Analysis
              </h6>

              <div className="row mb-2">
                <div className="col-md-6">
                  <p className="mb-2">
                    <strong>Successful:</strong>{' '}
                    <span className={`badge ${callData.call_analysis.call_successful ? 'bg-success' : 'bg-danger'}`}>
                      {callData.call_analysis.call_successful ? 'Yes' : 'No'}
                    </span>
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="mb-2">
                    <strong>Voicemail:</strong>{' '}
                    <span className={`badge ${callData.call_analysis.in_voicemail ? 'bg-warning' : 'bg-info'}`}>
                      {callData.call_analysis.in_voicemail ? 'Yes' : 'No'}
                    </span>
                  </p>
                </div>
              </div>

              {callData.call_analysis.user_sentiment && (
                <p className="mb-2">
                  <strong>User Sentiment:</strong>{' '}
                  <span className={`badge ${getSentimentBadge(callData.call_analysis.user_sentiment)}`}>
                    {callData.call_analysis.user_sentiment}
                  </span>
                </p>
              )}

              {callData.call_analysis.call_summary && (
                <>
                  <hr />
                  <h6 className="text-muted mt-2 mb-2">Summary</h6>
                  <p className="mb-0">{callData.call_analysis.call_summary}</p>
                </>
              )}

              {callData.call_analysis.custom_analysis_data && Object.keys(callData.call_analysis.custom_analysis_data).length > 0 && (
                <>
                  <hr />
                  <h6 className="text-muted">Custom Analysis</h6>
                  {Object.entries(callData.call_analysis.custom_analysis_data).map(([key, value]) => (
                    <p key={key} className="mb-1">
                      <strong>{key}:</strong> {value}
                    </p>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* Cost Information */}
        {isAnkrAdmin && callData.call_cost && (
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title text-primary mb-3">
                Cost Information
              </h6>
              <p className="mb-2">
                <strong>Total Cost:</strong>{' '}
                <span className="text-success">${(callData.call_cost.combined_cost / 100).toFixed(2)}</span>
              </p>

              {callData.call_cost.product_costs && callData.call_cost.product_costs.length > 0 && (
                <>
                  <hr />
                  <h6 className="text-muted mt-2 mb-2">Breakdown</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {callData.call_cost.product_costs.map((product, idx) => (
                          <tr key={idx}>
                            <td>{product.product}</td>
                            <td>${(product.cost / 100).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTranscriptTab = () => {
    if (!callData || !callData.transcript) return null;

    return (
      <div className="p-3">
        <VoiceAITranscriptViewer
          transcript={callData.transcript}
          transcriptObject={callData.transcript_object}
          isAnkrAdmin={isAnkrAdmin}
        />
      </div>
    );
  };

  const renderMetricsTab = () => {
    if (!callData) return null;

    return (
      <div className="p-3">
        {/* Latency Metrics */}
        {callData.latency && (
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title text-primary mb-3">
                Latency Metrics
              </h6>
              <div className="row">
                {Object.entries(callData.latency).map(([key, metrics]) => (
                  <div key={key} className="col-md-6 mb-3">
                    <h6 className="text-uppercase text-muted">{key}</h6>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <tbody>
                          <tr>
                            <td><strong>P50:</strong></td>
                            <td>{metrics.p50?.toFixed(2)}ms</td>
                          </tr>
                          <tr>
                            <td><strong>P90:</strong></td>
                            <td>{metrics.p90?.toFixed(2)}ms</td>
                          </tr>
                          <tr>
                            <td><strong>P95:</strong></td>
                            <td>{metrics.p95?.toFixed(2)}ms</td>
                          </tr>
                          <tr>
                            <td><strong>P99:</strong></td>
                            <td>{metrics.p99?.toFixed(2)}ms</td>
                          </tr>
                          <tr>
                            <td><strong>Min:</strong></td>
                            <td>{metrics.min?.toFixed(2)}ms</td>
                          </tr>
                          <tr>
                            <td><strong>Max:</strong></td>
                            <td>{metrics.max?.toFixed(2)}ms</td>
                          </tr>
                          <tr>
                            <td><strong>Samples:</strong></td>
                            <td>{metrics.num}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* LLM Token Usage */}
        {callData.llm_token_usage && (
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title text-primary mb-3">
                LLM Token Usage
              </h6>
              <p className="mb-2">
                <strong>Average Tokens:</strong> {callData.llm_token_usage.average}
              </p>
              <p className="mb-2">
                <strong>Number of Requests:</strong> {callData.llm_token_usage.num_requests}
              </p>
              {callData.llm_token_usage.values && callData.llm_token_usage.values.length > 0 && (
                <>
                  <hr />
                  <h6 className="text-muted">Token Usage per Request</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {callData.llm_token_usage.values.map((value, idx) => (
                      <span key={idx} className="badge bg-secondary">
                        {value}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Tool Calls */}
        {callData.tool_calls && callData.tool_calls.length > 0 && (
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title text-primary mb-3">
                Tool Calls
              </h6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Tool</th>
                      <th>Type</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callData.tool_calls.map((tool, idx) => (
                      <tr key={idx}>
                        <td>{tool.name}</td>
                        <td><span className="badge bg-info">{tool.type}</span></td>
                        <td>{tool.start_time_sec}s</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMediaTab = () => {
    if (!callData) return null;

    return (
      <div className="p-3">
        {/* Recording URLs */}
        {(callData.recording_url || callData.recording_multi_channel_url) && (
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title text-primary mb-3">
                Recording
              </h6>

              {callData.recording_url && (
                <div className="mb-3">
                  <h6 className="text-muted">Standard Recording</h6>
                  <audio controls className="w-100 mb-2">
                    <source src={callData.recording_url} type="audio/wav" />
                    Your browser does not support the audio element.
                  </audio>
                  <a
                    href={callData.recording_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    <i className="fas fa-download me-1"></i>
                    Download
                  </a>
                </div>
              )}

              {callData.recording_multi_channel_url && (
                <div className="mb-3">
                  <h6 className="text-muted">Multi-channel Recording</h6>
                  <audio controls className="w-100 mb-2">
                    <source src={callData.recording_multi_channel_url} type="audio/wav" />
                    Your browser does not support the audio element.
                  </audio>
                  <a
                    href={callData.recording_multi_channel_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    <i className="fas fa-download me-1"></i>
                    Download
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Public Log */}
        {isAnkrAdmin && callData.public_log_url && (
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title text-primary mb-3">
                Public Log
              </h6>
              <a
                href={callData.public_log_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary"
              >Click here to view log <i className="fas fa-external-link-alt ml-2"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRawDataTab = () => {
    if (!callData) return null;

    return (
      <div className="p-3">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="card-title text-primary mb-0">
                Raw JSON Data
              </h6>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => copyToClipboard(JSON.stringify(callData, null, 2), 'JSON')}
              >
                <i className={`fas ${copied === 'JSON' ? 'fa-check' : 'fa-copy'} me-1`}></i>
                {copied === 'JSON' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="bg-dark text-light p-3 rounded" style={{ maxHeight: '600px', overflow: 'auto' }}>
              {JSON.stringify(callData, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'summary', label: 'Summary', icon: 'fa-info-circle' },
    { id: 'transcript', label: 'Transcript', icon: 'fa-file-alt' },
    ...(isAnkrAdmin ? [{ id: 'metrics', label: 'Metrics', icon: 'fa-chart-bar' }] : []),
    { id: 'media', label: 'Media', icon: 'fa-headphones' },
    ...(isAnkrAdmin ? [{ id: 'raw', label: 'Raw Data', icon: 'fa-code' }] : [])
  ];

  return (
    <SidebarPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Activity Details"
      showCloseButton={true}
      style={{ width: '800px', maxWidth: '95vw' }}
    >
      {/* Retry message banner - shown during retry attempts */}
      {retryMessage && (
        <div className="alert alert-warning alert-dismissible fade show m-3" role="alert">
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Retrying...</span>
            </div>
            <span>{retryMessage}</span>
          </div>
        </div>
      )}

      {/* Error banner with retry button */}
      {error && !loading && (
        <div className="alert alert-danger m-3" role="alert">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => loadCallData(true)}
              title="Retry loading event data"
            >
              <i className="fas fa-sync-alt me-1"></i>
              Retry
            </button>
          </div>
        </div>
      )}

      {loading && !retryMessage ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading call data...</p>
        </div>
      ) : callData ? (
        <>
          {/* Tabs */}
          <ul className="nav nav-tabs px-3 pt-3" role="tablist">
            {tabs.map(tab => (
              <li key={tab.id} className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                  type="button"
                  role="tab"
                >
                  <i className={`fas ${tab.icon} me-1`}></i>
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'summary' && renderSummaryTab()}
            {activeTab === 'transcript' && renderTranscriptTab()}
            {isAnkrAdmin && activeTab === 'metrics' && renderMetricsTab()}
            {activeTab === 'media' && renderMediaTab()}
            {isAnkrAdmin && activeTab === 'raw' && renderRawDataTab()}
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <i className="fas fa-phone-slash fa-3x text-muted mb-3"></i>
          <p className="text-muted">No call data available</p>
        </div>
      )}
    </SidebarPanel>
  );
};

RetellCallViewer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  callId: PropTypes.string,
  callData: PropTypes.object
};

export default RetellCallViewer;
